import { Container, Text } from "@medusajs/ui"
import { useRef, type ClipboardEvent, type KeyboardEvent, type ReactNode } from "react"
import { KeelLockup, usePageTitle } from "@/keel/logo"

/**
 * The frame for every signed-out / sign-in step: password, two-step code, two-step setup,
 * invitation and password reset. One look for all of them: the KEEL lockup above a white card.
 */
export function AuthShell({ title, subtitle, children, footer, wide = false }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  usePageTitle(title.replace(/ to Keel$/, ""))
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-y-6 bg-[var(--k-surface)] px-4 py-10">
      <KeelLockup width={160} />
      <Container className={`tf-card w-full ${wide ? "max-w-[440px]" : "max-w-[400px]"} p-6 sm:p-8`}>
        <div className="mb-6 flex flex-col gap-y-1">
          <h1 className="k-h2">{title}</h1>
          {subtitle ? <p className="k-body text-[var(--k-ink-muted)]">{subtitle}</p> : null}
        </div>
        {children}
      </Container>
      {footer}
      <Text size="xsmall" className="text-ui-fg-muted">Keel Admin</Text>
    </div>
  )
}

/**
 * Six boxes for a one-time code. Typing moves to the next box, Backspace goes back, a pasted or
 * autofilled code (iOS / Android "from Messages") fills all six, and `onComplete` fires when full.
 */
export function CodeInput({ value, onChange, onComplete, disabled, length = 6, label = "Verification code" }: { value: string; onChange: (v: string) => void; onComplete?: (v: string) => void; disabled?: boolean; length?: number; label?: string }) {
  const refs = useRef<(HTMLInputElement | null)[]>([])
  const digits = value.padEnd(length, " ").slice(0, length).split("")
  const set = (next: string, focus: number) => {
    const v = next.replace(/\D/g, "").slice(0, length)
    onChange(v)
    refs.current[Math.min(focus, length - 1)]?.focus()
    if (v.length === length) onComplete?.(v)
  }
  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[i].trim() && i > 0) {
      e.preventDefault()
      set(value.slice(0, i - 1), i - 1)
    } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus()
    else if (e.key === "ArrowRight" && i < length - 1) refs.current[i + 1]?.focus()
  }
  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const t = e.clipboardData.getData("text").replace(/\D/g, "")
    if (!t) return
    e.preventDefault()
    set(t, t.length)
  }
  return (
    <div role="group" aria-label={label} className="flex justify-center gap-2">
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el
          }}
          aria-label={`${label}, digit ${i + 1} of ${length}`}
          inputMode="numeric"
          autoComplete={i === 0 ? "one-time-code" : "off"}
          // The first box takes a whole autofilled code; the rest take one digit each.
          maxLength={i === 0 ? length : 1}
          autoFocus={i === 0}
          disabled={disabled}
          value={d.trim()}
          onPaste={onPaste}
          onKeyDown={(e) => onKey(i, e)}
          onFocus={(e) => e.currentTarget.select()}
          onChange={(e) => {
            const typed = e.target.value.replace(/\D/g, "")
            if (!typed) return set(value.slice(0, i) + value.slice(i + 1), i)
            if (typed.length > 1) return set(value.slice(0, i) + typed, i + typed.length)
            set(value.slice(0, i) + typed + value.slice(i + 1), i + 1)
          }}
          className="h-12 w-11 rounded-lg border border-ui-border-base bg-ui-bg-field text-center font-mono text-xl text-ui-fg-base shadow-borders-base outline-none transition focus:border-ui-border-interactive focus:shadow-borders-interactive-with-active disabled:opacity-50 sm:w-12"
        />
      ))}
    </div>
  )
}
